import styled from 'styled-components';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { List } from 'foundations-components/transitional/components/List';
import { Tile } from 'foundations-components/transitional/components/Tile';
import { useCallback, useState, useMemo } from 'react';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { getInvoiceProperty } from '../../utils/property';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDiv = styled.div.withConfig({
  displayName: "InvoiceEmailReminderModal__StyledDiv",
  componentId: "uwk2ho-0"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    width: '50%',
    padding: theme.space['250']
  };
});
const InvoiceDateProperty = ({
  label,
  value
}) => /*#__PURE__*/_jsxs(TFlex, {
  direction: "column",
  children: [/*#__PURE__*/_jsx("div", {
    children: label
  }), /*#__PURE__*/_jsx("div", {
    "data-test-id": "invoice-date-property-value",
    children: value ? /*#__PURE__*/_jsx(FormattedDateTime, {
      value: value,
      format: "ll"
    }) : '--'
  })]
});
const InvoiceEmailReminderModal = ({
  invoice,
  onSave,
  onClose,
  loading,
  failed
}) => {
  const upcomingReminderDates = getInvoiceProperty(invoice, 'hs_upcoming_reminder_dates');
  const lastReminderDate = getInvoiceProperty(invoice, 'hs_last_reminder_sent_date');
  const invoiceDueDate = getInvoiceProperty(invoice, 'hs_due_date');
  const initialReminders = useMemo(() => upcomingReminderDates ? upcomingReminderDates.split(';').map(date => ({
    checked: true,
    date: +date
  })) : [], [upcomingReminderDates]);
  const [upcomingReminders, setUpcomingReminders] = useState(initialReminders);
  const handleOnChangeEmailReminder = useCallback(({
    target: {
      value: reminder,
      checked
    }
  }) => {
    const updatedUpcomingReminders = upcomingReminders.map(upcomingReminder => {
      if (upcomingReminder.date === +reminder) {
        return Object.assign({}, upcomingReminder, {
          checked
        });
      }
      return upcomingReminder;
    });
    setUpcomingReminders(updatedUpcomingReminders);
  }, [upcomingReminders]);
  const hasReminderChanges = useMemo(() => upcomingReminders.some(reminder => !reminder.checked), [upcomingReminders]);
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "edit-reminders-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.editReminders.header"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsxs(TFlex, {
        direction: "column",
        children: [/*#__PURE__*/_jsx(Tile, {
          inlineSize: "100%",
          children: /*#__PURE__*/_jsxs(TFlex, {
            direction: "row",
            align: "center",
            children: [/*#__PURE__*/_jsx(StyledDiv, {
              "data-test-id": "email-reminder-invoiceDueDate",
              children: /*#__PURE__*/_jsx(InvoiceDateProperty, {
                label: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "invoices.components.modals.editReminders.invoiceDueDate"
                }),
                value: invoiceDueDate ? +invoiceDueDate : undefined
              })
            }), /*#__PURE__*/_jsx(StyledDiv, {
              "data-test-id": "email-reminder-lastSentReminderDate",
              children: /*#__PURE__*/_jsx(InvoiceDateProperty, {
                label: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "invoices.components.modals.editReminders.lastReminderSentDate"
                }),
                value: lastReminderDate ? +lastReminderDate : undefined
              })
            })]
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlock: "200",
          as: "div",
          children: /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "invoices.components.modals.editReminders.upcomingReminders"
            })
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlock: "200",
          as: "div",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoices.components.modals.editReminders.upcomingRemindersHelp"
          })
        }), /*#__PURE__*/_jsx(List, {
          wrapChildren: true,
          children: upcomingReminders.map((reminder, i) => /*#__PURE__*/_jsx(Checkbox, {
            "data-test-id": "upcoming-reminder-checkbox",
            onChange: handleOnChangeEmailReminder,
            checked: reminder.checked,
            value: reminder.date,
            children: /*#__PURE__*/_jsx(FormattedDateTime, {
              value: reminder.date,
              format: "LL"
            })
          }, i))
        })]
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        use: "primary",
        onClick: () => onSave(invoice.objectId, upcomingReminders),
        loading: loading,
        failed: failed,
        disabled: !hasReminderChanges,
        "data-test-id": "save-email-reminders-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.editReminders.save"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.editReminders.cancel"
        })
      })]
    })]
  });
};
export default InvoiceEmailReminderModal;